<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <h2 class="mb-0 font-weight-bold">
          Calendário Pessoal
        </h2>
        <small class="text-muted">Gerencie seus agendamentos e compromissos</small>
      </div>
    </div>

    <!-- Tabs -->
    <b-tabs
      v-model="activeTab"
      pills
      card
    >
      <!-- Dashboard Tab -->
      <b-tab active>
        <template #title>
          <feather-icon
            icon="LayoutIcon"
            size="14"
            class="mr-1"
          />
          Visão Geral
        </template>

        <div class="dashboard-content">
          <CalendarDashboardStats />
          <CalendarDashboardStatus class="mt-3" />
          <b-row class="mt-3">
            <b-col lg="6">
              <CalendarDashboardRecent @viewAll="viewAllActivities" />
            </b-col>
            <b-col lg="6">
              <CalendarDashboardUpcoming @newAppointment="openNewAppointment" />
            </b-col>
          </b-row>
        </div>
      </b-tab>

      <!-- Calendar Tab -->
      <b-tab>
        <template #title>
          <feather-icon
            icon="CalendarIcon"
            size="14"
            class="mr-1"
          />
          Calendário
        </template>

        <div class="google-calendar-shell">
          <!-- Header -->
          <CalendarHeader
            :current-month-year="currentMonthYear"
            :value="view"
            @previousMonth="previousMonth"
            @nextMonth="nextMonth"
            @goToToday="goToToday"
            @input="view = $event"
          />

          <div class="calendar-body">
            <!-- Sidebar -->
            <CalendarSidebar
              :selected-tipos="selectedTipos"
              :selected-statuses="selectedStatuses"
              :selected-motivos="selectedMotivos"
              :show-all-motivos="showAllMotivos"
              :selected-executivos="selectedExecutivos"
              :is-not-executivo-cobranca="isNotExecutivoCobranca"
              :executivos-com-agendamentos="executivosComAgendamentos"
              @newEvent="openEventSidebar()"
              @toggleTipo="toggleTipo"
              @toggleStatus="toggleStatus"
              @toggleMotivo="toggleMotivo"
              @clearMotivos="clearMotivos"
              @uncheckAllMotivos="uncheckAllMotivos"
              @toggleExecutivo="toggleExecutivo"
            />

            <!-- Calendar Grid -->
            <div class="calendar-content">
              <!-- Month View -->
              <CalendarMonthView
                v-if="view === 'month'"
                :week-days="weekDays"
                :calendar-days="calendarDays"
                :get-events-for-day="getEventsForDay"
                @dayClick="handleDayClick"
                @eventClick="openEventSidebar"
                @openDayView="openDayView"
              />

              <!-- Week View -->
              <CalendarWeekView
                v-else-if="view === 'week'"
                :week-view-days="weekViewDays"
                :get-events-for-hour="getEventsForHour"
                @timeSlotClick="handleTimeSlotClick"
                @eventClick="openEventSidebar"
                @openDayView="openDayView"
              />

              <!-- Day View -->
              <CalendarDayView
                v-else-if="view === 'day'"
                :current-date="currentDate"
                :get-events-for-day-hour="getEventsForDayHour"
                @timeSlotClick="handleTimeSlotClick"
                @eventClick="openEventSidebar"
              />

              <!-- List View -->
              <CalendarListView
                v-else-if="view === 'list'"
                :filtered-events="filteredEvents"
                :sorted-events="sortedEvents"
                @eventClick="openEventSidebar"
              />
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <!-- Event Sheet -->
    <EventSheet
      v-model="isEventSidebarOpen"
      :selected-event="selectedEvent"
      :event-form="eventForm"
      :status-form="statusForm"
      :cliente-search="clienteSearch"
      :cliente-suggestions="clienteSuggestions"
      :highlighted-cliente-index="highlightedClienteIndex"
      :is-searching-clientes="isSearchingClientes"
      :show-cliente-search-dropdown="showClienteSearchDropdown"
      :show-cliente-suggestions="showClienteSuggestions"
      :is-not-executivo-cobranca="isNotExecutivoCobranca"
      :executivo-options-for-form="executivoOptionsForForm"
      :saving="saving"
      :updating-status="updatingStatus"
      @update:clienteSearch="clienteSearch = $event"
      @saveEvent="handleSaveEvent"
      @deleteEvent="handleDeleteEvent"
      @updateStatus="handleUpdateStatus"
      @clienteSearchFocus="handleClienteSearchFocus"
      @clienteSearchBlur="handleClienteSearchBlur"
      @navigateClienteDown="navigateClienteDown"
      @navigateClienteUp="navigateClienteUp"
      @selectHighlightedCliente="selectHighlightedCliente"
      @clearClienteSelection="clearClienteSelection"
      @selectCliente="selectCliente"
    />

    <!-- Loading Overlay -->
    <div
      v-if="loading"
      class="loading-overlay"
    >
      <b-spinner variant="primary" />
    </div>
  </div>
</template>

<script>
import {
  BSpinner,
  BTabs,
  BTab,
  BRow,
  BCol,
} from 'bootstrap-vue'
import CalendarHeader from './components/CalendarHeader.vue'
import CalendarSidebar from './components/CalendarSidebar.vue'
import CalendarMonthView from './components/CalendarMonthView.vue'
import CalendarWeekView from './components/CalendarWeekView.vue'
import CalendarDayView from './components/CalendarDayView.vue'
import CalendarListView from './components/CalendarListView.vue'
import EventSheet from './components/EventSheet.vue'
import CalendarDashboardStats from './dashboard/CalendarDashboardStats.vue'
import CalendarDashboardStatus from './dashboard/CalendarDashboardStatus.vue'
import CalendarDashboardRecent from './dashboard/CalendarDashboardRecent.vue'
import CalendarDashboardUpcoming from './dashboard/CalendarDashboardUpcoming.vue'
import useCalendarPessoal from './useCalendarPessoal'

export default {
  name: 'CalendarPessoal',
  components: {
    BSpinner,
    BTabs,
    BTab,
    BRow,
    BCol,
    CalendarHeader,
    CalendarSidebar,
    CalendarMonthView,
    CalendarWeekView,
    CalendarDayView,
    CalendarListView,
    EventSheet,
    CalendarDashboardStats,
    CalendarDashboardStatus,
    CalendarDashboardRecent,
    CalendarDashboardUpcoming,
  },
  setup() {
    return useCalendarPessoal()
  },
  data() {
    return {
      activeTab: 0,
      shouldOpenCreateModal: false,
    }
  },
  methods: {
    openNewAppointment() {
      this.activeTab = 1
      this.shouldOpenCreateModal = true
    },
    viewAllActivities() {
      this.activeTab = 1
    },
  },
}
</script>

<style scoped>
.google-calendar-shell {
  position: relative;
  min-height: 600px;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid #dadce0;
  background: #f8fafd;
  color: #202124;
  box-shadow: 0 1px 3px rgba(60,64,67,0.15);
  font-family: 'Google Sans', Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.calendar-body {
  display: flex;
  background: #fff;
}

.calendar-content {
  min-width: 0;
  flex: 1;
  background: #fff;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
